import { useCallback } from 'react';
import moment from 'moment';
// redux
import { useSelector } from '../redux/store';
import useDemoMode, { buildPracticeNameMasker } from './useDemoMode';

// kebab-case slug safe for filenames; collapses anything non-alphanumeric to '-'
const slugify = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

// Accepts a Date or a date string (e.g. 'YYYY-MM-DD' or ISO) → 'YYYY-MM-DD' (or '').
// Uses moment (local time) rather than Date.toISOString() so a Date at local
// midnight in a timezone ahead of UTC (e.g. BST = UTC+1) isn't rolled back to
// the previous calendar day in the filename.
const fmtDate = (d) => {
    if (!d) return '';
    const m = moment(d);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

/**
 * Builds a descriptive CSV export filename for the analytics reports:
 *   `<reportSlug>_<practice>_<start>_to_<end>.csv`
 *
 * - Practice segment is the current practice name (masked as "Practice #<id>"
 *   when demo mode is on so real names aren't leaked into downloaded files).
 *   Pass `opts.practiceName` to override (e.g. 'all-practices' for
 *   cross-practice comparison reports), or '' to omit the practice segment.
 * - Date segment uses the report's selected range; if a range isn't available
 *   it falls back to the export date so the file is still uniquely named.
 *
 * Usage:
 *   const buildExportName = useReportExportFilename();
 *   link.download = buildExportName('team-members', dateRange);
 *   // → team-members_smile-rooms_2026-01-01_to_2026-03-31.csv
 */
export default function useReportExportFilename() {
    const practice = useSelector((state) => state.practice?.practice);
    const [demoMode] = useDemoMode();

    return useCallback(
        (reportSlug, dateRange, opts = {}) => {
            const parts = [slugify(reportSlug) || 'report'];

            let { practiceName } = opts;
            if (practiceName === undefined) {
                practiceName = demoMode
                    ? buildPracticeNameMasker([], demoMode)(practice, practice?.id)
                    : practice?.name;
            }
            const practiceSlug = slugify(practiceName);
            if (practiceSlug) parts.push(practiceSlug);

            const start = fmtDate(dateRange?.startDate);
            const end = fmtDate(dateRange?.endDate);
            if (start && end) parts.push(`${start}_to_${end}`);
            else parts.push(fmtDate(new Date()));

            return `${parts.join('_')}.csv`;
        },
        [practice, demoMode]
    );
}
